// frontend/src/components/common/ui/StatsGrid.tsx
// Unified stats component to replace all module-level stats components

import React from 'react';
import { LucideIcon } from 'lucide-react';

// Color theme configurations
export type StatsColorTheme = 
  | 'blue' 
  | 'green' 
  | 'red' 
  | 'yellow' 
  | 'purple' 
  | 'orange' 
  | 'indigo' 
  | 'gray'
  | 'pink'
  | 'amber';

// Layout configurations
export type StatsLayout = 'basic' | 'detailed' | 'compact' | 'card';
export type StatsSize = 'sm' | 'md' | 'lg';

// Individual stat item interface
export interface StatItem {
  id?: string;
  label: string;
  value: number | string;
  icon?: LucideIcon;
  color?: StatsColorTheme;
  description?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string;
  };
  action?: {
    label: string;
    onClick: () => void;
  };
  // For percentage bars
  percentage?: number;
  maxValue?: number;
  // For custom styling
  className?: string;
  bgColor?: string;
  textColor?: string;
  iconColor?: string;
}

// Main component props
export interface StatsGridProps {
  stats: StatItem[];
  isLoading?: boolean;
  layout?: StatsLayout;
  size?: StatsSize;
  columns?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  };
  showTooltips?: boolean;
  showTrends?: boolean;
  showIcons?: boolean;
  className?: string;
  title?: string;
  description?: string;
  // Loading skeleton configuration
  loadingItems?: number;
  // Custom grid classes for specific layouts
  gridClassName?: string;
  // Container props
  containerClassName?: string;
}

// Color theme mapping
const colorThemes: Record<StatsColorTheme, {
  text: string;
  bg: string;
  border: string;
  icon: string;
  accent: string;
}> = {
  blue: {
    text: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    accent: 'text-blue-600'
  },
  green: {
    text: 'text-green-700',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-600',
    accent: 'text-green-600'
  },
  red: {
    text: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    accent: 'text-red-600'
  },
  yellow: {
    text: 'text-yellow-700',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    accent: 'text-yellow-600'
  },
  purple: {
    text: 'text-purple-700',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: 'text-purple-600',
    accent: 'text-purple-600'
  },
  orange: {
    text: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'text-orange-600',
    accent: 'text-orange-600'
  },
  indigo: {
    text: 'text-indigo-700',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    icon: 'text-indigo-600',
    accent: 'text-indigo-600'
  },
  gray: {
    text: 'text-gray-700',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'text-gray-600',
    accent: 'text-gray-600'
  },
  pink: {
    text: 'text-pink-700',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    icon: 'text-pink-600',
    accent: 'text-pink-600'
  },
  amber: {
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    accent: 'text-amber-600'
  }
};

// Size configurations
const sizeConfigs = {
  sm: {
    text: 'text-lg',
    icon: 'h-4 w-4',
    padding: 'p-3',
    label: 'text-xs',
    description: 'text-xs'
  },
  md: {
    text: 'text-xl sm:text-2xl',
    icon: 'h-5 w-5 sm:h-6 sm:w-6',
    padding: 'p-4',
    label: 'text-sm',
    description: 'text-sm'
  },
  lg: {
    text: 'text-2xl sm:text-3xl',
    icon: 'h-6 w-6 sm:h-8 sm:w-8',
    padding: 'p-6',
    label: 'text-base',
    description: 'text-base'
  }
};

// Loading skeleton component
const LoadingStatCard: React.FC<{ size: StatsSize; className?: string }> = ({ 
  size, 
  className = '' 
}) => {
  const config = sizeConfigs[size];
  
  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${config.padding} animate-pulse ${className}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`${config.icon} bg-gray-200 rounded`}></div>
        <div className="h-4 bg-gray-200 rounded flex-1"></div>
      </div>
      <div className="h-8 bg-gray-200 rounded mb-1"></div>
      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
    </div>
  );
};

// Individual stat card component
const StatCard: React.FC<{
  stat: StatItem;
  layout: StatsLayout;
  size: StatsSize;
  showIcons: boolean;
  showTrends: boolean;
  showTooltips: boolean;
}> = ({ stat, layout, size, showIcons, showTrends, showTooltips }) => {
  const config = sizeConfigs[size];
  const theme = stat.color ? colorThemes[stat.color] : colorThemes.gray;
  
  // Use custom colors if provided, otherwise use theme colors
  const bgColor = stat.bgColor || theme.bg;
  const textColor = stat.textColor || theme.text;
  const iconColor = stat.iconColor || theme.icon;
  
  const Icon = stat.icon;
  
  const baseClasses = `
    bg-white rounded-lg border border-gray-200 ${config.padding} 
    hover:shadow-md transition-shadow duration-200
    ${stat.className || ''}
  `;
  
  const contentClasses = layout === 'compact' ? 'text-center' : '';
  
  return (
    <div className={baseClasses} title={showTooltips ? stat.description : undefined}>
      {layout === 'detailed' ? (
        // Detailed layout with icon and content side by side
        <div className="flex items-center gap-3">
          {showIcons && Icon && (
            <div className={`p-2 rounded-lg ${bgColor}`}>
              <Icon className={`${config.icon} ${iconColor}`} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className={`font-bold ${textColor} ${config.text}`}>
              {stat.value}
            </div>
            <div className={`text-gray-600 ${config.label} truncate`}>
              {stat.label}
            </div>
            {stat.description && (
              <div className={`text-gray-500 ${config.description} mt-1`}>
                {stat.description}
              </div>
            )}
          </div>
          {showTrends && stat.trend && (
            <div className={`text-right ${config.description}`}>
              <div className={stat.trend.isPositive ? 'text-green-600' : 'text-red-600'}>
                {stat.trend.isPositive ? '+' : ''}{stat.trend.value}%
              </div>
              {stat.trend.label && (
                <div className="text-gray-500">{stat.trend.label}</div>
              )}
            </div>
          )}
        </div>
      ) : layout === 'compact' ? (
        // Compact layout - centered
        <div className={contentClasses}>
          {showIcons && Icon && (
            <div className="flex justify-center mb-2">
              <Icon className={`${config.icon} ${iconColor}`} />
            </div>
          )}
          <div className={`font-bold ${textColor} ${config.text} mb-1`}>
            {stat.value}
          </div>
          <div className={`text-gray-600 ${config.label}`}>
            {stat.label}
          </div>
        </div>
      ) : layout === 'card' ? (
        // Card layout with background color
        <div className={`${bgColor} ${theme.border} border rounded-lg p-4`}>
          <div className="flex items-center justify-between mb-2">
            {showIcons && Icon && (
              <Icon className={`${config.icon} ${iconColor}`} />
            )}
            {showTrends && stat.trend && (
              <div className={`${config.description} ${stat.trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {stat.trend.isPositive ? '+' : ''}{stat.trend.value}%
              </div>
            )}
          </div>
          <div className={`font-bold ${textColor} ${config.text} mb-1`}>
            {stat.value}
          </div>
          <div className={`text-gray-600 ${config.label}`}>
            {stat.label}
          </div>
          {stat.percentage !== undefined && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>{stat.percentage}%</span>
                {stat.maxValue && <span>of {stat.maxValue}</span>}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${theme.accent.replace('text-', 'bg-')}`}
                  style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        // Basic layout (default)
        <div>
          <div className="flex items-center gap-2 mb-2">
            {showIcons && Icon && (
              <Icon className={`${config.icon} ${iconColor}`} />
            )}
            <span className={`font-medium text-gray-600 ${config.label}`}>
              {stat.label}
            </span>
          </div>
          <div className="flex items-end justify-between">
            <div className={`font-bold ${textColor} ${config.text}`}>
              {stat.value}
            </div>
            {showTrends && stat.trend && (
              <div className={`${config.description} ${stat.trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {stat.trend.isPositive ? '+' : ''}{stat.trend.value}%
              </div>
            )}
          </div>
          {stat.percentage !== undefined && (
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
              <div 
                className={`h-1 rounded-full transition-all duration-300 ${theme.accent.replace('text-', 'bg-')}`}
                style={{ width: `${Math.min(stat.percentage, 100)}%` }}
              />
            </div>
          )}
          {stat.description && (
            <div className={`text-gray-500 ${config.description} mt-1`}>
              {stat.description}
            </div>
          )}
          {stat.action && (
            <button
              onClick={stat.action.onClick}
              className={`mt-2 text-xs ${theme.accent} hover:underline`}
            >
              {stat.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Main StatsGrid component
export const StatsGrid: React.FC<StatsGridProps> = ({
  stats,
  isLoading = false,
  layout = 'basic',
  size = 'md',
  columns = { mobile: 2, tablet: 3, desktop: 4 },
  showTooltips = false,
  showTrends = false,
  showIcons = true,
  className = '',
  title,
  description,
  loadingItems = 4,
  gridClassName,
  containerClassName = ''
}) => {
  // Generate grid classes based on columns configuration
  const getGridClasses = () => {
    if (gridClassName) return gridClassName;
    
    const { mobile = 2, tablet = 3, desktop = 4 } = columns;
    return `grid grid-cols-${mobile} md:grid-cols-${tablet} lg:grid-cols-${desktop} gap-4`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`${containerClassName} ${className}`}>
        {title && (
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
        )}
        <div className={getGridClasses()}>
          {Array.from({ length: loadingItems }).map((_, index) => (
            <LoadingStatCard key={index} size={size} />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!stats || stats.length === 0) {
    return (
      <div className={`${containerClassName} ${className}`}>
        {title && (
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
        )}
        <div className="text-center py-8 text-gray-500">
          No statistics available
        </div>
      </div>
    );
  }

  return (
    <div className={`${containerClassName} ${className}`}>
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
      )}
      <div className={getGridClasses()}>
        {stats.map((stat, index) => (
          <StatCard
            key={stat.id || index}
            stat={stat}
            layout={layout}
            size={size}
            showIcons={showIcons}
            showTrends={showTrends}
            showTooltips={showTooltips}
          />
        ))}
      </div>
    </div>
  );
};

export default StatsGrid;